import { v4 as uuidv4 } from 'uuid'
import { reduxStore } from 'components/ReduxAppWrapper'
import { createAnalyticsPayload } from '~core/Analytics/createAnalyticsPayload'
import { Logger } from '~core/Logger'
import pageVisibility from '~core/PageVisibility'
import { flushQueue } from '~core/util/flushQueue'
import { UrlsConfig } from '~types/commons'
import { sendAnalytics } from '~utils/onfidoApi'
import {
  BasePerformanceEvent,
  PerformanceEvent,
  PerformanceEventQueue,
} from './types'
import type { OptionalPick } from '~types/util'

const logger = new Logger({ labels: ['performance_analytics'] })

let token: string | undefined
let urls: UrlsConfig
let enabled: boolean | undefined

class PerformanceAnalytics {
  private eventsMap: Map<string, PerformanceEvent> = new Map()
  private queue: PerformanceEventQueue[] = []

  constructor() {
    pageVisibility.addListener(this.pageVisiblityHandler)
    reduxStore.subscribe(this.reduxStoreHandler)

    window.addEventListener('beforeunload', this.clearEvents)
    window.addEventListener('close', this.clearEvents)
  }

  private pageVisiblityHandler = (visible: boolean) => {
    if (!visible) this.clearEvents()
  }

  private reduxStoreHandler = () => {
    const store = reduxStore.getState()

    token = store.globals.token
    urls = store.globals.urls
    enabled =
      store.globals.sdkConfiguration?.sdk_features
        ?.enable_performance_analytics ?? false

    if (token && urls && enabled) {
      this.flushQueue()
    }
  }

  // Public api
  public trackStart<T extends BasePerformanceEvent>(
    event: PerformanceEvent,
    ...[startProperties]: OptionalPick<T, 'start_properties'>
  ): void {
    if (!enabled) return
    this.eventsMap.set(event.uuid, {
      ...event,
      time: performance.now(),
      properties: {
        ...(event.properties || {}),
        ...(startProperties || {}),
      },
    })
  }

  public trackEnd<T extends BasePerformanceEvent>(
    endEvent: PerformanceEvent,
    ...[endProperties]: OptionalPick<T, 'end_properties'>
  ): void {
    const startEvent = this.eventsMap.get(endEvent.uuid)

    if (!enabled || !token) return
    if (!startEvent) {
      logger.error(`The start event was not found for ${endEvent.eventName}`)
      return
    }

    this.queue.push({
      timestamp: Date.now(),
      ...startEvent,
      ...endEvent,
      properties: {
        duration: Math.round(performance.now() - startEvent.time!),
        ...(startEvent.properties || {}),
        ...(endProperties || {}),
      },
    })

    this.eventsMap.delete(endEvent.uuid)
  }

  public clearEvents = () => {
    logger.debug('Clearing events and sending to backend')
    this.eventsMap.clear()
    this.flushQueue()
  }

  public flushQueue = () => {
    if (!this.queue.length) return
    flushQueue<PerformanceEventQueue>(this.queue, 5, this.sendToBackend)
    this.queue = []
  }

  private sendToBackend = (items: PerformanceEventQueue[]) => {
    if (!token) return
    const events = items.map(({ timestamp, ...item }) =>
      createAnalyticsPayload({
        event: item.eventName,
        event_uuid: item.eventName === item.uuid ? undefined : item.uuid,
        event_time: new Date(timestamp).toISOString(),
        properties: item.properties,
      })
    )

    const url = `${urls.onfido_api_url}/v4/sdk/performance`
    sendAnalytics(url, JSON.stringify({ events }), token)
  }
}

export default new PerformanceAnalytics()

export const createSyncPerformanceEvent = <T extends BasePerformanceEvent>(
  eventName: T['eventName'],
  ...[properties]: T['properties'] extends never ? [] : [T['properties']]
): PerformanceEvent => ({
  uuid: eventName,
  eventName,
  properties: properties || {},
})

export const createAsyncPerformanceEvent = <T extends BasePerformanceEvent>(
  eventName: T['eventName'],
  ...[properties]: T['properties'] extends never ? [] : [T['properties']]
): PerformanceEvent => ({
  uuid: uuidv4(),
  eventName,
  properties: properties || {},
})
